import React, { useEffect, useRef, useState } from 'react';
import { getMobIconInlineSVG } from '../../../../rendering/mobs/MobSVGRegistry';
import { getMobVisualConfig } from '../../../../rendering/mobs/MobVisualArchetypes';
import { computeIdlePose, createPose } from '../../../../rendering/mobs/ArchetypeBehavior';
import { prefersReducedMotion } from '../../../../systems/accessibility/motionPrefs';

interface MobPreviewProps {
  mobId: string;
  size?: number;
  /** Accessible name; the art is decorative when omitted. */
  label?: string;
}

/**
 * The mob's own art, breathing with the same idle pose the world renderer uses
 * (computeIdlePose), driven by requestAnimationFrame into a CSS transform —
 * no Pixi, no React re-render per frame. Static under reduced motion.
 */
export const MobPreview: React.FC<MobPreviewProps> = ({ mobId, size = 56, label }) => {
  const [svg, setSvg] = useState<string | null>(null);
  const bodyRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    let live = true;
    setSvg(null);
    getMobIconInlineSVG(mobId, size).then(markup => { if (live) setSvg(markup); }).catch(() => { /* art is optional */ });
    return () => { live = false; };
  }, [mobId, size]);

  useEffect(() => {
    const body = bodyRef.current;
    const cfg = getMobVisualConfig(mobId);
    if (!svg || !body || !cfg || prefersReducedMotion() || typeof requestAnimationFrame !== 'function') return;
    const pose = createPose();
    // The world pose is in world pixels for a ~64px body; scale it to this preview.
    const k = size / 64;
    let frame = 0;
    const tick = (t: number) => {
      computeIdlePose(cfg, t, pose);
      body.style.transform = `translate(${(pose.dx * k).toFixed(2)}px, ${(pose.dy * k).toFixed(2)}px) `
        + `rotate(${pose.angleDeg.toFixed(2)}deg) scale(${pose.scaleXMul.toFixed(3)}, ${pose.scaleYMul.toFixed(3)})`;
      body.style.opacity = pose.alphaMul.toFixed(3);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [mobId, size, svg]);

  return <span className="npc-mob-preview" data-mob-preview={mobId} role={label ? 'img' : undefined}
    aria-label={label} aria-hidden={label ? undefined : true} style={{ width: size, height: size }}>
    <span ref={bodyRef} className="npc-mob-preview-body"
      dangerouslySetInnerHTML={svg ? { __html: svg } : undefined} />
  </span>;
};
